import { GridColDef } from "@mui/x-data-grid-pro";
import {
  ArrowLeftIcon,
  Box,
  DownIcon,
  EditIcon,
  GKButton,
  GKClientDataGrid,
  Grid2,
  IconButton,
  Typography,
} from "components";
import dayjs from "dayjs";
import DashboardLayout from "layouts/DashboardLayout";
import { useNavigate, useParams } from "react-router-dom";
import {
  OrderBatchStatus,
  SystematicPlanInstallment,
  SystematicPlanStatus,
  useGetSystematicPlanByIdQuery,
} from "store/features/MFOrders";
import { useAppSelector } from "store/store";
import { moneyCommaSeparator } from "utils";
import { extraColumnsField } from "utils/helpers";
import useDocumentTitle from "utils/useDocumentTitle";

const PLAN_STATUS_STYLE: Record<
  SystematicPlanStatus,
  { bg: string; color: string; label: string }
> = {
  active: { bg: "#e8f3ec", color: "#15803d", label: "Active" },
  paused: { bg: "#f9f2dd", color: "#e6b532", label: "Paused" },
  cancelled: { bg: "#ececec", color: "#697586", label: "Cancelled" },
};

const ORDER_STATUS_STYLE: Record<
  OrderBatchStatus,
  { bg: string; color: string; label: string }
> = {
  in_process: { bg: "#f9f2dd", color: "#e6b532", label: "In-Process" },
  completed: { bg: "#e8f3ec", color: "#15803d", label: "Completed" },
  partially_completed: {
    bg: "#e8f3ec",
    color: "#15803d",
    label: "Part Completed",
  },
  failed: { bg: "#fde8e8", color: "#c35245", label: "Failed" },
  cancelled: { bg: "#ececec", color: "#697586", label: "Cancelled" },
};

const PlanStatusPill = ({ status }: { status: SystematicPlanStatus }) => {
  const s = PLAN_STATUS_STYLE[status] ?? {
    bg: "#ececec",
    color: "#697586",
    label: status,
  };
  return (
    <Box
      sx={{
        alignItems: "center",
        bgcolor: s.bg,
        borderRadius: "6px",
        display: "inline-flex",
        px: "10px",
        py: "4px",
      }}
    >
      <Typography
        color={s.color}
        fontSize={10}
        fontWeight={600}
        sx={{ letterSpacing: "0.3px", textTransform: "uppercase" }}
      >
        {s.label}
      </Typography>
    </Box>
  );
};

const OrderStatusPill = ({ status }: { status: OrderBatchStatus }) => {
  const s = ORDER_STATUS_STYLE[status] ?? {
    bg: "#ececec",
    color: "#697586",
    label: status,
  };
  return (
    <Box
      sx={{
        alignItems: "center",
        bgcolor: s.bg,
        borderRadius: "6px",
        display: "inline-flex",
        px: "10px",
        py: "4px",
      }}
    >
      <Typography
        color={s.color}
        fontSize={10}
        fontWeight={600}
        sx={{ letterSpacing: "0.3px", textTransform: "uppercase" }}
      >
        {s.label}
      </Typography>
    </Box>
  );
};

const InfoColumn = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
    <Typography color="var(--neutral-500)" fontSize={12} fontWeight={500}>
      {label}
    </Typography>
    <Typography color="var(--black-900)" fontSize={14} fontWeight={600}>
      {value}
    </Typography>
  </Box>
);

const toNum = (s?: string | null) => {
  if (s == null || s === "") return null;
  const n = Number(s);
  return Number.isNaN(n) ? null : n;
};

const planShortFor = (t?: string) => {
  if (t === "swp") return "SWP";
  if (t === "stp") return "STP";
  return "SIP";
};

const formatFrequency = (f?: string | null) =>
  f ? f.charAt(0).toUpperCase() + f.slice(1) : "-";

const SystematicPlanDetailsPage = () => {
  useDocumentTitle("Order Details");
  const navigate = useNavigate();
  const { planId } = useParams<{ planId: string }>();

  const { data: plan, isFetching } = useGetSystematicPlanByIdQuery(
    planId ?? "",
    { skip: !planId },
  );

  const companies = useAppSelector((state) => state.app.companies);
  const schemeName = (() => {
    if (!plan?.gcode) return plan?.schemeName ?? "-";
    const match = (companies ?? []).find(
      (c: any) => c?.gridCode === plan.gcode,
    );
    return match?.name ?? plan.schemeName ?? plan.gcode;
  })();

  const planShort = planShortFor(plan?.planType);

  const columns: GridColDef<SystematicPlanInstallment>[] = [
    {
      ...extraColumnsField(`${planShort} Date`, "installmentNumber", 160, 0),
      sortable: false,
      renderCell: (params) => (
        <Typography color="var(--black-900)" fontSize={13} fontWeight={500}>
          #{params?.row?.installmentNumber ?? "-"}
        </Typography>
      ),
    },
    {
      ...extraColumnsField("Execution Date", "transactionDate", 160, 0),
      sortable: false,
      renderCell: (params) => (
        <Typography color="var(--black-900)" fontSize={13} fontWeight={500}>
          {params?.row?.transactionDate
            ? dayjs(params.row.transactionDate).format("DD/MM/YYYY")
            : "-"}
        </Typography>
      ),
    },
    {
      ...extraColumnsField(`${planShort} Amount`, "amount", 160, 0),
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const v = toNum(params?.row?.netAmount) ?? toNum(params?.row?.amount);
        return (
          <Typography color="#15803d" fontSize={13} fontWeight={600}>
            {v != null ? `+₹${moneyCommaSeparator(v)}` : "-"}
          </Typography>
        );
      },
    },
    {
      ...extraColumnsField("QTY/Units", "units", 140, 0),
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const v =
          toNum(params?.row?.unitsAllotted) ?? toNum(params?.row?.units);
        return (
          <Typography color="var(--black-900)" fontSize={13} fontWeight={500}>
            {v != null ? moneyCommaSeparator(v) : "-"}
          </Typography>
        );
      },
    },
    {
      ...extraColumnsField("NAV", "nav", 120, 0),
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const v = toNum(params?.row?.nav);
        return (
          <Typography color="var(--black-900)" fontSize={13} fontWeight={500}>
            {v != null ? `₹${moneyCommaSeparator(v)}` : "-"}
          </Typography>
        );
      },
    },
    {
      ...extraColumnsField("Order Status", "status", 140, 0),
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) =>
        params?.row?.status ? (
          <OrderStatusPill status={params.row.status} />
        ) : null,
    },
  ];

  return (
    <DashboardLayout title="Order Details" loading={isFetching}>
      <Grid2 container spacing={2}>
        <Grid2 size={12} display="flex" alignItems="center" gap="8px" mb="4px">
          <IconButton
            sx={{ p: "4px" }}
            onClick={() => navigate(-1)}
            aria-label="Back"
          >
            <ArrowLeftIcon height={20} width={20} />
          </IconButton>
          <Typography color="var(--black-900)" fontSize={18} fontWeight={600}>
            Order Details
          </Typography>
        </Grid2>

        {plan && (
          <>
            <Grid2 size={12}>
              <Box
                sx={{
                  bgcolor: "var(--grey-bg-100)",
                  borderRadius: "16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  p: "20px",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{ alignItems: "center", display: "flex", gap: "8px" }}
                  >
                    <PlanStatusPill status={plan.status} />
                    <Typography
                      color="var(--neutral-500)"
                      fontSize={12}
                      fontWeight={500}
                    >
                      •
                    </Typography>
                    <Typography
                      color="var(--neutral-700)"
                      fontSize={12}
                      fontWeight={500}
                    >
                      {plan.createdAt
                        ? dayjs(plan.createdAt).format("DD MMM YYYY, hh:mm A")
                        : "-"}
                    </Typography>
                  </Box>
                  <GKButton
                    variant="text"
                    startIcon={<EditIcon height={14} width={14} />}
                    endIcon={<DownIcon height={14} width={14} />}
                    sx={{
                      color: "var(--black-900)",
                      fontSize: 12,
                      fontWeight: 600,
                      textTransform: "none",
                    }}
                  >
                    Edit Order Details
                  </GKButton>
                </Box>

                <Box sx={{ display: "flex", gap: "48px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <Typography
                      color="var(--black-900)"
                      fontSize={16}
                      fontWeight={700}
                    >
                      {plan.portfolioName}
                    </Typography>
                    <Typography
                      color="var(--neutral-500)"
                      fontSize={12}
                      fontWeight={500}
                    >
                      {plan.portfolioName} • {plan.portfolioCode}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      borderLeft: "1px solid var(--neutral-100)",
                      pl: "24px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                    }}
                  >
                    <Typography
                      color="var(--neutral-500)"
                      fontSize={12}
                      fontWeight={500}
                    >
                      Folio No.
                    </Typography>
                    <Typography
                      color="var(--black-900)"
                      fontSize={14}
                      fontWeight={600}
                    >
                      {plan.folioNumber ?? "-"}
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  color="var(--black-900)"
                  fontSize={16}
                  fontWeight={600}
                >
                  {schemeName}
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gap: "32px",
                    gridTemplateColumns: "repeat(5, max-content)",
                  }}
                >
                  <InfoColumn
                    label="Order Type"
                    value={
                      plan.orderType
                        ? plan.orderType.charAt(0).toUpperCase() +
                          plan.orderType.slice(1)
                        : "-"
                    }
                  />
                  <InfoColumn label="Plan Type" value={planShort} />
                  <InfoColumn
                    label="Frequency"
                    value={formatFrequency(plan.frequency)}
                  />
                  <InfoColumn
                    label="No. of Installments"
                    value={
                      plan.isPerpetual
                        ? "Perpetual"
                        : plan.installmentsTarget != null
                          ? String(plan.installmentsTarget)
                          : "-"
                    }
                  />
                  <InfoColumn
                    label={`${planShort} Order Date`}
                    value={
                      plan.startDate
                        ? dayjs(plan.startDate).format("DD/MM/YYYY")
                        : "-"
                    }
                  />
                </Box>
              </Box>
            </Grid2>

            <Grid2 size={12}>
              <GKClientDataGrid
                name="systematicPlanInstallments"
                columns={columns}
                rows={plan.installments ?? []}
                getRowId={(row) => row.id}
                columnsButton={false}
                pagination={false}
              />
            </Grid2>
          </>
        )}
      </Grid2>
    </DashboardLayout>
  );
};

export default SystematicPlanDetailsPage;
